import { useCallback, useEffect, useRef, useState } from 'react'
import { useDetectClickOutside } from 'react-detect-click-outside'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'
import useGooglePlaceDetails from 'tennders-common/form/hooks/useGooglePlaceDetails'
import TextInput, { TextInputProps } from 'tennders-common/form/components/new-inputs/TextInput'
import LocationSuggestions from 'tennders-common/form/components/new-inputs/LocationSuggestions'
import GLoading from 'tennders-market/common/components/element/GLoading'
import DOMPurify from 'dompurify'
import useRoutesSearch from 'tennders-common/hooks/useRoutesSearch'
import { INPUT_CHARACTER_LIMITS } from 'tennders-stracture/libs/constants'

const center = { lat: 50.012100, lng: 20.985842 }

const defaultBounds = {
  north: center.lat + 10,
  south: center.lat - 10,
  east: center.lng + 10,
  west: center.lng - 10
}

export type LocationProps = TextInputProps & { isSmartLocation ?: boolean } & { asInput: boolean } & { google: boolean }

const Location = (props: LocationProps) => {
  const {
    onSet,
    value,
    children,
    isSmartLocation,
    ...rest
  } = props
  const activelyListening = useRef(false)
  const [suggestions, setSuggestions] = useState([])
  const [isSmart, setIsSmart] = useState(isSmartLocation)
  const [text, settext] = useState<string | boolean>('')
  const closeDropdown = () => setSuggestions([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const ref = useDetectClickOutside({ onTriggered: closeDropdown })

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading
  } = usePlacesService({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
    language: 'en',
    options: {
      types: ['(regions)'],
      bounds: defaultBounds,
      input: undefined
    }
  })
  const { getDetails } = useGooglePlaceDetails({ placesService, onSet, setSuggestions })

  const searchGoogle = (value) => {
    if (value.length > 0) {
      getPlacePredictions({ input: value })
    }
  }

  useRoutesSearch(text, setSuggestions, setIsSmart, searchGoogle, value)

  const smartSuggestionSelected = (item) => {
    const location = { ...item, smart: true }
    onSet(location)
    setSuggestions([])
  }

  const renderSmartSuggestions = () => {
    if (isPlacePredictionsLoading) {
      return (
        <div style={{
          position: 'absolute',
          top: '50%',
          right: -30,
          left: '50%'
        }}>
          <GLoading color="#000" size={14} />
        </div>
      )
    }
    if (suggestions.length === 0 || text === '') {
      return null
    }
    return (
      <ul
        ref={ref}
        className="smart-location-suggestions"
        style={{
          position: 'absolute',
          top: '55px',
          left: '0',
          padding: 12,
          width: 300,
          border: '1px solid #ddd',
          boxShadow: '0 0 10px rgb(0 0 0 / 15%)',
          backgroundColor: 'white',
          zIndex: 99999
        }}
      >
        {suggestions?.map((item, i) => (
          <li
            style={{
              cursor: 'pointer',
              minHeight: 35,
              borderLeft: i === selectedIndex ? '2px solid #0879cf' : 'none'
            }}
            key={item.label}
            onClick={() => smartSuggestionSelected(item)}
          >
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item?.label) }} />
          </li>
        ))}
      </ul>
    )
  }

  const onTextChanged = (value) => {
    settext(value)
    setIsSmart(isSmartLocation)
    if (onSet) {
      onSet(value)
    }
  }

  const handleArrowDownKey = () => {
    // Your Tab key specific functionality here

    setSelectedIndex(selectedIndex => {
      let newSelectedIndex = selectedIndex === -1 ? 0 : selectedIndex + 1
      if (newSelectedIndex > suggestions.length - 1) {
        newSelectedIndex = 0
      }
      return newSelectedIndex
    })
  }

  const handleArrowUpKey = () => {
    // Your Tab key specific functionality here

    setSelectedIndex(selectedIndex => {
      let newSelectedIndex = selectedIndex === -1 ? 0 : selectedIndex - 1
      if (newSelectedIndex <= 0) {
        newSelectedIndex = 0
      }
      return newSelectedIndex
    })
  }

  const handleEnterKey = () => {
    if (selectedIndex !== -1) {
      const item = suggestions[selectedIndex]
      if (typeof item !== 'undefined') {
        getDetails({ item })
      }
    }
  }

  const handleKeyPress = useCallback((event) => {
    switch (event.key) {
      case 'Enter':
      // Call a specific function for the Enter key
        handleEnterKey()
        break
      case 'ArrowDown':
        // Call a specific function for the Arrow Down key
        handleArrowDownKey()
        break
      case 'ArrowUp':
        // Call a specific function for the Arrow Up key
        handleArrowUpKey()
        break
      default:
        break
    }

    return false
  }, [selectedIndex, suggestions])
  const hasSuggestions = suggestions.length > 0
  useEffect(() => {
    if (hasSuggestions && !isPlacePredictionsLoading) {
      document.addEventListener('keydown', handleKeyPress)
    } else {
      document.removeEventListener('keydown', handleKeyPress)
    }
    // Don't forget to remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [suggestions, selectedIndex, isPlacePredictionsLoading])

  useEffect(() => {
    const valueText = value?.label || value
    if (valueText !== text) {
      settext(valueText)
    }
  }, [value])

  useEffect(() => {
    // fetch place details for the first element in placePredictions array
    if (placePredictions.length > 0) {
      setSuggestions(placePredictions)
    }
  }, [placePredictions])

  return (
    <div className="pos-rel">
      <TextInput
        value={text}
        autoComplete="new-password"
        onSet={onTextChanged}
        maxLength={INPUT_CHARACTER_LIMITS.LOCATION}
        {...rest}
      />
      {isSmart
        ? renderSmartSuggestions()
        : (
          <LocationSuggestions
            ref={ref}
            selectedIndex={selectedIndex}
            loading={isPlacePredictionsLoading}
            suggestions={suggestions}
            setSuggestionSelected={(item) => getDetails({ item })}
          />
        )}
      {children}
    </div>
  )
}
export default Location
